import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { searchStories, SearchStoriesParams, backendSources, isSourceAccessible, BackendSource } from "@/shared/api/firehose";
import firehoseSSEClient from "@/shared/api/firehose/firehoseSSEClient";
import { NewsFeedItem } from "@/shared/api/firehose/models";
import { firehoseFilterMatcher, firehosePortfolioMatcher } from "../utils";
import { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import { useGetAppRoles } from "@/hooks/useGetAppRoles";
import { useHasInternalNewsquawkUserRole } from "@/hooks/useHasInternalNewsquawkUserRole";

const MAX_SEARCH_RESULTS = 300;

export const useSearchFirehose = () => {
  const [searchParams, setSearchParams] = useState<SearchStoriesParams>({});
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [page, setPage] = useState(1);
  const [allResults, setAllResults] = useState<NewsFeedItem[]>([]);
  const [hasMore, setHasMore] = useState(true);

  const roles = useGetAppRoles();
  const isInternalUser = useHasInternalNewsquawkUserRole();
  const accessibleSources = useMemo(
    () => backendSources.filter((s) => isSourceAccessible(s, roles, isInternalUser)),
    [roles, isInternalUser]
  );

  const queryClient = useQueryClient();

  const {
    data: _searchResults,
    error,
    isError,
    isLoading,
    isSuccess,
    refetch,
  } = useQuery({
    queryKey: ["searchFirehose", searchParams],
    queryFn: async ({ signal }) => {
      const results = await searchStories({ ...searchParams, signal });
      return results;
    },
    enabled: isSearchActive && Object.keys(searchParams).length > 0,
    staleTime: 0,
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // Accumulate results when new page is loaded
  useEffect(() => {
    if (_searchResults && _searchResults.length >= 0 && isSearchActive) {
      const currentOffset = searchParams.offset ?? 0;
      const initialPage = searchParams.initialPage ?? 1;
      const initialOffset = (initialPage - 1) * (searchParams.limit ?? 50);

      if (currentOffset === initialOffset) {
        // Initial page for this search - replace results
        setAllResults(_searchResults);
        setHasMore(_searchResults.length > 0);
      } else {
        // Subsequent pages - append results
        // Remove duplicates based on id
        setAllResults((prev) => {
          const uniqueResults = _searchResults.filter(
            (story) => prev.findIndex((s) => s.id === story.id) === -1
          );
          const updated = [...prev, ...uniqueResults];
          return updated.length > MAX_SEARCH_RESULTS ? updated.slice(0, MAX_SEARCH_RESULTS) : updated;
        });
        // If we got an empty list, there are no more results
        setHasMore(_searchResults.length > 0);
      }
    }
  }, [
    _searchResults,
    isSearchActive,
    searchParams.offset,
    searchParams.limit,
    searchParams.initialPage,
  ]);

  const searchResults = isSearchActive ? allResults : [];

  const searchFirehose = useCallback(async (params: SearchStoriesParams) => {
    const startPage = params.initialPage ?? 1;
    const limit = params.limit ?? 50;
    const initialOffset = (startPage - 1) * limit;

    // Normalize source to array (guards against string being passed instead of array)
    const sourceArray = params.source
      ? Array.isArray(params.source) ? params.source : [params.source]
      : undefined;

    // Gate sources: filter passed sources to accessible ones, or default to all accessible
    const gatedSources = sourceArray
      ? sourceArray.filter((s) => accessibleSources.includes(s as BackendSource))
      : ([...accessibleSources] as (BackendSource | "allStories")[]);

    const searchPayload: SearchStoriesParams = {
      ...params,
      source: gatedSources,
      offset: initialOffset,
      limit: limit,
      initialPage: startPage,
    };

    setSearchParams(searchPayload);
    setIsSearchActive(true);
    setPage(startPage);
    setAllResults([]);
    setHasMore(true);
  }, [accessibleSources]);

  const loadNextPage = useCallback(() => {
    const newPage = page + 1;
    const newOffset = (newPage - 1) * (searchParams.limit ?? 50);

    setSearchParams((prev) => ({
      ...prev,
      offset: newOffset,
    }));
    setPage(newPage);
  }, [page, searchParams.limit]);

  const clearSearchResults = useCallback(() => {
    // Cancel any pending queries and clear the cache
    queryClient.cancelQueries({ queryKey: ["searchFirehose"] });
    queryClient.removeQueries({ queryKey: ["searchFirehose"] });

    setIsSearchActive(false);
    setSearchParams({});
    setPage(1);
    setAllResults([]);
    setHasMore(true);
  }, [queryClient]);

  // Filter SSE results to only include stories matching search params
  useEffect(() => {
    if (!isSearchActive) return;

    const unsubscribe = firehoseSSEClient.subscribe(
      "message",
      (data: NewsFeedItem) => {
        if (isSearchActive) {
          // Check if the incoming story matches the search criteria
          // source is always set to accessible sources (gated in searchFirehose)
          if (
            searchParams.source &&
            !searchParams.source?.includes(data.backend_source)
          ) {
            return;
          }

          let matched = false;

          if (searchParams.advanceFilter) {
            // Merge UI-selected portfolios into the CategoryQuery
            // so firehoseFilterMatcher evaluates them together, matching
            // the backend behavior in searchStories.
            const mergedFilter: CategoryQuery = {
              ...searchParams.advanceFilter,
              portfolios: [
                ...(searchParams.advanceFilter.portfolios || []),
                ...(searchParams.portfolios || []),
              ],
            };
            matched = firehoseFilterMatcher(data, mergedFilter);
          } else if (searchParams.portfolios && searchParams.portfolios.length > 0) {
            matched = firehosePortfolioMatcher(data, searchParams.portfolios);
          } else if (searchParams.q) {
            // Simple text search - check if query appears in title or body
            const query = searchParams.q.toLowerCase();
            const titleMatch = data.title?.toLowerCase().includes(query);
            const bodyMatch = data.body?.toLowerCase().includes(query) ?? false;
            matched = titleMatch || bodyMatch;
          } else {
            // No content filter (advanceFilter, portfolios, or q), only source filter
            // Story already passed source check above, so include it
            matched = true;
          }

          if (matched) {
            // If it matches, add it to the search results
            queryClient.setQueryData<NewsFeedItem[]>(
              ["searchFirehose", searchParams],
              (oldData = []) => {
                // Check if story already exists (deduplicate)
                const alreadyExists = oldData.some(
                  (story) => story.id === data.id
                );
                if (alreadyExists) {
                  return oldData;
                }
                const updated = [data, ...oldData];
                return updated.length > MAX_SEARCH_RESULTS ? updated.slice(0, MAX_SEARCH_RESULTS) : updated;
              }
            );
          }
        }
      }
    );

    return unsubscribe;
  }, [isSearchActive, queryClient, searchParams]);
  // Cleanup on unmount

  return {
    searchResults,
    error,
    isError,
    isLoading,
    isSuccess,
    searchFirehose,
    clearSearchResults,
    refetch,
    loadNextPage,
    page,
    hasMore,
  };
};
